import { Controller } from "@hotwired/stimulus";
import axios from 'axios';

export default class extends Controller {
  static values = {
    taggableId: Number,
    taggableType: String,
    tagOptions: Array
  }
  static targets = ["pillsContainer", "croppedCount", "croppedDropdown", "divider", "addTag", "tagInput", "dropdown"]

  connect() {
    this.recalc = this.recalc.bind(this);
    window.addEventListener('resize', this.recalc);
    this.recalc();

    // Hide dropdown when clicking outside the component
    document.addEventListener('click', this.hideDropdown.bind(this));
    this.element.addEventListener('focus-tag-input', this.focusInput.bind(this));
  }

  disconnect() {
    window.removeEventListener('resize', this.recalc);
    document.removeEventListener('click', this.hideDropdown.bind(this));
    this.element.removeEventListener('focus-tag-input', this.focusInput.bind(this));
  }

  recalc() {
    // Reset all pills to visible only
    const pills = Array.from(this.pillsContainerTarget.children);
    pills.forEach(pill => pill.style.display = '');

    let croppedCount = 0;
    let lastVisibleIndex = pills.length - 1;
    const containerRect = this.pillsContainerTarget.getBoundingClientRect();
    this.hiddenTags = []; // Track hidden tags for dropdown

    // First pass: check if any pills overflow horizontally OR vertically
    let needsCropping = false;
    for (let i = 0; i < pills.length; i++) {
      const pill = pills[i];
      const pillRect = pill.getBoundingClientRect();
      // Check horizontal overflow OR vertical overflow (bottom exceeds container bottom)
      if (pillRect.right > containerRect.right || pillRect.bottom > containerRect.bottom) {
        needsCropping = true;
        break;
      }
    }

    if (needsCropping) {
      // Temporarily show the cropped count element to measure its width
      this.croppedCountTarget.textContent = '+99'; // Use worst-case width
      this.croppedCountTarget.style.display = '';
      const croppedCountWidth = this.croppedCountTarget.getBoundingClientRect().width;

      // Reserve space for the cropped count element
      const availableWidth = containerRect.right - croppedCountWidth - 8; // 8px margin

      // Second pass: find how many pills fit with reserved space
      for (let i = 0; i < pills.length; i++) {
        const pill = pills[i];
        const pillRect = pill.getBoundingClientRect();

        // Check if pill overflows horizontally OR vertically
        if (pillRect.right > availableWidth || pillRect.bottom > containerRect.bottom) {
          lastVisibleIndex = i - 1;
          break;
        }
      }
    }

    // Hide all pills after lastVisibleIndex and collect hidden tag names
    for (let i = lastVisibleIndex + 1; i < pills.length; i++) {
      pills[i].style.setProperty('display', 'none', 'important');
      croppedCount++;

      // Extract tag name from the pill (remove the close icon text)
      const spanElement = pills[i].querySelector('span');
      const tagText = spanElement && spanElement.textContent && spanElement.textContent.trim();
      if (tagText) {
        this.hiddenTags.push(tagText);
      }
    }

    // Show cropped count if needed
    if (croppedCount > 0) {
      this.croppedCountTarget.textContent = `+${croppedCount}`;
      this.croppedCountTarget.style.display = '';
    } else {
      this.croppedCountTarget.textContent = '';
      this.croppedCountTarget.style.display = 'none';
    }

    // Hide divider if there are no pills to display
    if (pills.length === 0) {
      this.dividerTarget.style.display = 'none';
    } else {
      this.dividerTarget.style.display = '';
    }
  }

  // Call this method if the tag list changes
  tagListValueChanged() {
    this.recalc();

    const pills = Array.from(this.pillsContainerTarget.children);
    if (pills.length >= 25) {
      this.addTagTarget.style.display = 'none';
      this.dividerTarget.style.display = 'none';
    } else {
      this.addTagTarget.style.display = '';
      this.dividerTarget.style.display = '';
    }
  }

  removeTag(event) {
    const pill = event.target.closest('.pill-tag');
    const tagToRemove = pill.textContent.trim();

    // Check if this tag is being removed from the dropdown
    const isFromDropdown = pill.closest('.dropdown-menu');

    const params = new URLSearchParams({
      taggable_type: this.taggableTypeValue,
      taggable_id: this.taggableIdValue,
      tag: tagToRemove
    });

    // If removing from dropdown, immediately remove the item for better UX
    if (isFromDropdown) {
      pill.remove();

      // Update hidden tags array
      if (this.hiddenTags) {
        this.hiddenTags = this.hiddenTags.filter(tag => tag !== tagToRemove);

        // Update the "+N" count immediately
        if (this.hiddenTags.length > 0) {
          this.croppedCountTarget.textContent = `+${this.hiddenTags.length}`;
        } else {
          // Hide dropdown and cropped count if no more hidden tags
          this.croppedDropdownTarget.style.display = 'none';
          this.croppedCountTarget.style.display = 'none';
        }
      }
    }

    axios.delete(`/manage/tags?${params}`, {
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(res => {
      // Update the pills content with the returned HTML
      this.pillsContainerTarget.innerHTML = res.data.html;

      // Update tag options with the fresh data from server
      this.tagOptionsValue = res.data.tag_options;

      // Recalculate which pills to show/hide
      this.tagListValueChanged();

      // Dispatch custom event for tag changes
      this.element.dispatchEvent(new CustomEvent('tag-management:changed'));
    })
    .catch(err => {
      console.error("Error removing tag: ", err.response.data || err.message || err);
    });
  }

  formatTag(tag) {
    return tag.toLowerCase()                // Convert to lowercase
      .replace(/[^a-z0-9_ ]/g, '')         // Remove symbols except underscores and spaces
      .replace(/ /g, '_')                  // Replace spaces with underscores
      .slice(0, 25);                       // Limit to 25 characters
  }

  searchTags(event) {
    const rawQuery = event.target.value;
    const formattedQuery = this.formatTag(rawQuery);

    // Update the input to show the formatted version of the search text
    if (rawQuery !== formattedQuery) {
      event.target.value = formattedQuery;
    }

    const filteredTags = this.tagOptionsValue.filter(tag =>
      tag.toLowerCase().includes(formattedQuery)
    );

    this.showDropdown(filteredTags, formattedQuery);
  }

  // Updates dropdown options
  showDropdown(tags, query) {
    const dropdown = this.dropdownTarget;
    dropdown.innerHTML = '';

    // Show matching existing tags
    tags.forEach(tag => {
      const option = document.createElement('div');
      option.className = 'dropdown-item';
      option.style.cursor = 'pointer';
      option.textContent = tag;
      // Use mousedown so that the click event can fire when focus is lost
      option.addEventListener('mousedown', (e) => {
        e.preventDefault(); // Prevents the blur event from firing
        this.selectTag(tag);
      });
      dropdown.appendChild(option);
    });

    // If no exact match and query is not empty, show "Add new tag" option
    if (query && !tags.includes(query)) {
      const addNewOption = document.createElement('div');
      addNewOption.className = 'dropdown-item text-primary bg-white';
      addNewOption.style.cursor = 'pointer';
      addNewOption.innerHTML = `Add "${query}"`;
      // Use mousedown so that the click event can fire when focus is lost
      addNewOption.addEventListener('mousedown', (e) => {
        e.preventDefault(); // Prevents the blur event from firing
        this.selectTag(query);
      });
      dropdown.appendChild(addNewOption);
    }

    if (dropdown.children.length > 0) {
      dropdown.style.display = 'block';
    } else {
      dropdown.style.display = 'none';
    }
  }

  hideDropdown(event) {
    if (!this.element.contains(event.target)) {
      this.dropdownTarget.style.display = 'none';
      if (this.hasCroppedDropdownTarget) {
        this.croppedDropdownTarget.style.display = 'none';
      }
    }
  }

  // Add tag when hitting Enter
  handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = this.tagInputTarget.value.trim();
      if (value) {
        const formattedTag = this.formatTag(value);
        this.selectTag(formattedTag);
      }
    }
  }

  selectTag(tag) {
    const formattedTag = this.formatTag(tag);
    this.addTag(formattedTag);

    // Clear input and hide dropdown
    this.tagInputTarget.value = '';
    this.dropdownTarget.style.display = 'none';
  }

  addTag(tag) {
    const body = {
      taggable_type: this.taggableTypeValue,
      taggable_id: this.taggableIdValue,
      tag: tag
    };

    axios.post('/manage/tags', body, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    .then(res => {
      // Update the pills content with the returned HTML
      this.pillsContainerTarget.innerHTML = res.data.html;

      // Update tag options with the fresh data from server
      this.tagOptionsValue = res.data.tag_options;

      // Recalculate which pills to show/hide
      this.tagListValueChanged();

      // Dispatch custom event for tag changes
      this.element.dispatchEvent(new CustomEvent('tag-management:changed'));
    })
    .catch(err => {
      console.error("Error adding tag: ", err.response.data || err.message || err);
    });
  }

  clearInput(event) {
    // Clear the input content when user clicks outside
    event.target.value = '';

    // Also hide the dropdown
    this.dropdownTarget.style.display = 'none';
  }

  toggleCroppedDropdown(event) {
    event.stopPropagation();

    if (!this.hiddenTags || this.hiddenTags.length === 0) {
      return;
    }

    const dropdown = this.croppedDropdownTarget;

    if (dropdown.style.display === 'block') {
      dropdown.style.display = 'none';
    } else {
      // Position dropdown relative to the cropped count element
      const croppedCountRect = this.croppedCountTarget.getBoundingClientRect();
      const containerRect = this.element.getBoundingClientRect();

      dropdown.style.position = 'absolute';
      dropdown.style.left = `${croppedCountRect.left - containerRect.left}px`;
      dropdown.style.top = `${croppedCountRect.bottom - containerRect.top}px`;

      // Populate dropdown with hidden tags
      dropdown.innerHTML = '';

      this.hiddenTags.forEach(tag => {
        const item = document.createElement('div');
        item.className = 'dropdown-item px-2 py-1 d-flex justify-content-between align-items-center pill-tag';

        const tagText = document.createElement('span');
        tagText.textContent = tag;

        const removeIcon = document.createElement('i');
        removeIcon.className = 'far fa-times ml-1 cursor-pointer';
        removeIcon.setAttribute('data-action', 'click->tag-management#removeTag');

        item.appendChild(tagText);
        item.appendChild(removeIcon);
        dropdown.appendChild(item);
      });

      dropdown.style.display = 'block';
    }
  }

  onInputClick(event) {
    const url = `/manage/tags/${this.taggableTypeValue}/${this.taggableIdValue}`;

    axios.get(url, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
    .then(res => {
      this.tagOptionsValue = res.data.tag_options;
    })
    .catch(err => {
      console.error("Error loading tags: ", err);
    });
  }

  focusInput() {
    // Unfortunately a timeout appears to be necessary here.
    setTimeout(() => {
      this.tagInputTarget.focus();
    }, 100);
  }
}
